import { useCallback } from 'react';
import { useAuth } from 'react-oidc-context';

import { keycloakConfig } from 'src/config';
import { userManager } from 'src/userManager';

function buildProviderLogoutUrl(idToken?: string): string | null {
  const { authority, clientId } = keycloakConfig;

  if (!(authority && clientId)) {
    return null;
  }

  const logoutUrl = new URL(`${authority}/protocol/openid-connect/logout`);
  logoutUrl.searchParams.set('client_id', clientId);
  logoutUrl.searchParams.set(
    'post_logout_redirect_uri',
    window.location.origin,
  );
  logoutUrl.searchParams.set('redirect_uri', window.location.origin);

  if (idToken) {
    logoutUrl.searchParams.set('id_token_hint', idToken);
  }

  return logoutUrl.toString();
}

export function useLogout(): {
  logout: () => Promise<void>;
} {
  const { removeUser, signoutRedirect } = useAuth();

  const logout = useCallback(async (): Promise<void> => {
    const currentUser = await userManager.getUser().catch(() => null);
    const providerLogoutUrl = buildProviderLogoutUrl(currentUser?.id_token);

    try {
      await removeUser().catch(() => undefined);
      if (providerLogoutUrl) {
        window.location.assign(providerLogoutUrl);
        return;
      }
    } catch {
      // Continue with OIDC client fallbacks below.
    }

    const args = {
      post_logout_redirect_uri: window.location.origin,
    };

    try {
      await signoutRedirect(args);
      return;
    } catch {
      // Fallback on default OIDC signout to match environments with strict logout settings.
    }

    try {
      await signoutRedirect();
      return;
    } catch {
      // Ignore local signout failure and continue with hard redirect.
    }

    window.location.replace(window.location.origin);
  }, [removeUser, signoutRedirect]);

  return { logout };
}
